import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import './i18n/config'; // i18n設定をインポート
import App from './App';
import { SpaPageAnalytics } from './components/analytics/SpaPageAnalytics';
import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './styles/fontAwesomeStability.css';
import { configureDataTablesErrorMode } from './utils/jqueryDatatablesShim';

configureDataTablesErrorMode();

/**
 * デプロイ直後など、古いタブが消えた Vite チャンクを取りに行くと
 * 「Failed to fetch dynamically imported module」になる。
 * 再読込で直るため、短時間に1回だけ自動リロードする（無限リロード防止）。
 * @see https://vitejs.dev/guide/build.html#load-error-handling
 */
const VITE_CHUNK_RELOAD_AT_KEY = 'kensuke:vite-chunk-reload-at';
const VITE_CHUNK_RELOAD_COOLDOWN_MS = 30_000;

if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    let lastAt = 0;
    try {
      lastAt = Number(sessionStorage.getItem(VITE_CHUNK_RELOAD_AT_KEY) || '0');
    } catch {
      /* private mode 等 */
    }
    if (Number.isFinite(lastAt) && Date.now() - lastAt < VITE_CHUNK_RELOAD_COOLDOWN_MS) {
      return;
    }
    try {
      sessionStorage.setItem(VITE_CHUNK_RELOAD_AT_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    window.location.reload();
  });
}

/** Solid アイコン用 webfont を先読みし、初回の空→表示フラッシュを減らす */
if (typeof document !== 'undefined' && document.fonts?.load) {
  void document.fonts.load('900 1em "Font Awesome 6 Free"').catch(() => {
    /* ignore */
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = (error as { httpStatus?: number })?.httpStatus;
        if (status === 429) return failureCount < 4;
        return failureCount < 1;
      },
      retryDelay: (attemptIndex, error) => {
        const status = (error as { httpStatus?: number })?.httpStatus;
        if (status === 429) return Math.min(30_000, 2_000 * 2 ** attemptIndex);
        return 1_000;
      },
      staleTime: 5 * 60 * 1000, // 5分
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <SpaPageAnalytics />
        <Suspense
          fallback={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100vh',
              fontSize: '1.2rem',
              color: '#666'
            }}>
              <div>
                <div className="spinner-border text-primary" role="status" style={{ marginRight: '1rem' }}>
                  <span className="visually-hidden">読み込み中...</span>
                </div>
                読み込み中...
              </div>
            </div>
          }
        >
          <App />
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>
);

